import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/shop", label: "SHOP" },
  { to: "/collections", label: "COLLECTIONS" },
  { to: "/lookbook", label: "LOOKBOOK" },
  { to: "/story", label: "OUR STORY" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setDrawerOpen, setSearchOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "border-b border-white/10 bg-ink/85 backdrop-blur-md" : "border-b border-transparent bg-transparent"}`}
      >
        <div className="flex items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="font-display text-xl font-extrabold lowercase tracking-tight text-bone">
            aligndept
          </Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`} className="font-mono text-[11px] tracking-[0.3em] text-bone/70 transition-colors hover:text-bone">
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <button data-testid="nav-search-button" onClick={() => setSearchOpen(true)} aria-label="Search" className="text-bone/70 transition-colors hover:text-bone">
              <Search className="h-4 w-4" />
            </button>
            <button data-testid="nav-account-button" onClick={() => navigate(user?.role === "admin" ? "/admin" : "/account")} aria-label="Account" className="text-bone/70 transition-colors hover:text-bone">
              <User className="h-4 w-4" />
            </button>
            <button data-testid="nav-cart-button" onClick={() => setDrawerOpen(true)} className="relative flex items-center gap-1.5 text-bone/70 transition-colors hover:text-bone">
              <ShoppingBag className="h-4 w-4" />
              <span data-testid="nav-cart-count" className="font-mono text-[10px] tracking-widest">
                ({count})
              </span>
            </button>
            <button data-testid="nav-menu-button" onClick={() => setMenuOpen(true)} aria-label="Menu" className="text-bone/70 lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
      {menuOpen && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-[60] flex flex-col bg-ink">
          <div className="flex items-center justify-between px-5 py-4">
            <span className="font-display text-xl font-extrabold lowercase text-bone">aligndept</span>
            <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="text-bone/70">
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
            {LINKS.map((l, i) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className="border-b border-white/10 py-5 font-display text-4xl font-extrabold uppercase tracking-tight text-bone"
              >
                <span className="mr-4 font-mono text-xs text-fade">0{i + 1}</span>
                {l.label}
              </Link>
            ))}
          </nav>
          <p className="px-8 pb-8 font-mono text-[10px] tracking-[0.3em] text-fade">NO COINCIDENCES.</p>
        </div>
      )}
    </>
  );
}
